import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Drawer as MuiDrawer, IconButton } from "@mui/material";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Dialog, Input, Loader, toastMessage } from "../utils";
import { useGetDockOperationsOverviewQuery, useReleaseDockBayMutation, useTerminateDockTripMutation } from "../../api/vmsSlice";
import { usePlantContext } from "../../context/plantContext";
import { useUserPermissions, useRoomForDevice } from "../../services/hooks";
import { useSnapshotUrl } from "./useSnapshotUrl";
import { useDockChangeRefetch, useTabVisible, DOCK_FALLBACK_POLL_MS } from "./useDockRefresh";
import DockTripOperations from "./DockTripOperations";
import UnassignedVisits from "./UnassignedBayVisits";
import ProvisionalTrips from "./ProvisionalTrips";
import PeakActivity from "./PeakActivity";
import RecentOperations from "./RecentOperations";
import DockUtilisation from "./DockUtilisation";
import DwellBreakdown from "./DwellBreakdown";
import { filterQueueRows, isQueueReserved, queueBayLabel } from "./queueFilters";
import "./DockOperations.css";

// Fallback only. The live value comes from the response's `thresholds`, which reads
// plantConfig.dockSlaMinutes, so this tile and the operations table cannot disagree.
const DOCK_SLA_BREACH_MINUTES = 90;
// The live board is what an operator watches; the analytics tab is what a supervisor reads. Kept in
// the URL so the analytics view is linkable and survives a refresh on a wall-mounted screen.
const DOCK_TABS = [
	{ key: "live", label: "Live Board" },
	{ key: "analytics", label: "Overview & Analytics" },
];
const STATUS_FILTERS = [
	{ key: "all", label: "All" },
	{ key: "OCCUPIED", label: "Occupied" },
	{ key: "RESERVED", label: "Reserved" },
	{ key: "VACANT", label: "Vacant" },
];

// Formats backend UTC timestamps in the plant timezone supplied with the response.
function formatDockTime(value, timezone) {
	if (!value) return "—";
	return new Intl.DateTimeFormat("en-MY", {
		timeZone: timezone,
		day: "2-digit",
		month: "short",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	}).format(new Date(value));
}

// Formats durations as a clock, or with explicit units for queue times.
function formatElapsed(milliseconds, withUnits = false) {
	const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	if (withUnits) {
		const parts = [];
		if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
		if (minutes > 0) parts.push(`${minutes} min`);
		if (seconds > 0 || parts.length === 0) parts.push(`${seconds} sec`);
		return parts.join(" ");
	}
	const pad = (value) => String(value).padStart(2, "0");
	return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

// Ticks once a second so an occupied Bay's dwell timer updates live without refetching the board.
function useElapsedSince(sinceIso) {
	const [, forceTick] = useState(0);
	useEffect(() => {
		if (!sinceIso) return undefined;
		const interval = setInterval(() => forceTick((tick) => tick + 1), 1000);
		return () => clearInterval(interval);
	}, [sinceIso]);
	return sinceIso ? Date.now() - new Date(sinceIso).getTime() : null;
}


// Renders a camera snapshot, resolving the backend's bare S3 key to a signed URL first.
function CameraSnapshot({ camera, checkpointName }) {
	const snapshotUrl = useSnapshotUrl(camera?.snapshotPath, camera?.snapshotAt);
	const [failed, setFailed] = useState(false);
	useEffect(() => setFailed(false), [snapshotUrl]);
	return (
		<div className="dock-camera-frame">
			{snapshotUrl && !failed
				? <img src={snapshotUrl} alt={`${checkpointName} latest camera snapshot`} onError={() => setFailed(true)} />
				: <CameraAltOutlinedIcon />}
		</div>
	);
}

// Shows one Vehicle Queue entry (READY offer or WAITING) inside the queue panel.
function QueueRow({ row, order, offeredBayName, reservationExpiresAt, onViewTimeline }) {
	const elapsedMs = useElapsedSince(row.inTime);
	const isReady = isQueueReserved(row);
	// A bumped trip keeps its original waiting time but loses its place, so the row has to say why it moved.
	const movedBack = Boolean(row.queuePriorityAt);
	const expiresInMs = reservationExpiresAt ? new Date(reservationExpiresAt).getTime() - Date.now() : null;
	const handleKeyDown = (event) => { if (event.key === "Enter" || event.key === " ") onViewTimeline(row.tripId); };
	return (
		<div
			role="button"
			tabIndex={0}
			className={`dock-queue-row dock-queue-row-${isReady ? "ready" : "waiting"}`}
			onClick={() => onViewTimeline(row.tripId)}
			onKeyDown={handleKeyDown}
			title={movedBack ? "Moved back — missed an earlier Bay offer" : isReady ? "Bay available — awaiting Gate In" : "Waiting for Bay"}
		>
			<span className="dock-queue-order">#{order}</span>
			<span className="dock-queue-plate">{row.vehicleNumber || "Unknown vehicle"}</span>
			<span className="dock-queue-reservation">
				{queueBayLabel(row, offeredBayName)}
			</span>
			<span className={`dock-queue-badge dock-queue-badge-${isReady ? "ready" : "waiting"}`}>{isReady ? "Requested for Dock In" : "WAITING"}</span>
			<span className="dock-queue-meta">
				Waiting {elapsedMs != null ? formatElapsed(elapsedMs, true) : "—"}
				{movedBack && <span className="dock-queue-moved"> · Moved back</span>}
				{isReady && expiresInMs > 0 && ` · offer expires in ${formatElapsed(expiresInMs, true)}`}
			</span>
		</div>
	);
}

// Lists vehicles waiting for a Bay in the effective priority order supplied by the backend.
function QueuePanel({ rows, checkpoints, onViewTimeline }) {
	const [selectedBay, setSelectedBay] = useState("all");
	const [vehicleSearch, setVehicleSearch] = useState("");
	const bayOptions = new Map(checkpoints.filter(item => String(item.checkpoint?.checkpointType).toLowerCase() === "bay")
		.map(({ checkpoint }) => [String(checkpoint._id), checkpoint.displayName]));
	for (const row of rows) {
		const id = row.allocation?.checkpointId;
		if (id && !bayOptions.has(String(id))) bayOptions.set(String(id), row.allocation.checkpointName || "Unnamed bay");
	}
	const filteredRows = filterQueueRows(rows, selectedBay, vehicleSearch);
	return (
		<div className="dock-queue-panel">
			<div className="dock-queue-panel-header">
				<h3>Vehicle Queue</h3>
				<span>{selectedBay === "all" && !vehicleSearch.trim() ? `${rows.length} waiting` : `${filteredRows.length} of ${rows.length} waiting`}</span>
			</div>
			<label className="dock-queue-filter">
				<span>Allocated bay</span>
				<select value={selectedBay} onChange={event => setSelectedBay(event.target.value)}>
					<option value="all">All bays</option>
					{[...bayOptions].map(([id, name]) => <option key={id} value={id}>{name}</option>)}
					<option value="unreserved">Bay not assigned</option>
				</select>
			</label>
			<label className="dock-queue-filter">
				<span>Vehicle no.</span>
				<input type="search" value={vehicleSearch} placeholder="Search vehicle number"
					onChange={event => setVehicleSearch(event.target.value)} />
			</label>
			{filteredRows.length ? (
				<div className="dock-queue-list">
					{filteredRows.map(({ row, order }) => {
						const offered = checkpoints.find((item) => String(item.checkpoint?._id) === String(row.allocation?.checkpointId));
						return (
							<QueueRow
								key={row.tripId}
								row={row}
								order={order}
								offeredBayName={offered?.checkpoint?.displayName}
								reservationExpiresAt={offered?.liveState?.reservationExpiresAt}
								onViewTimeline={onViewTimeline}
							/>
						);
					})}
				</div>
			) : <div className="dock-queue-empty">{rows.length ? "No queued vehicles match this filter" : "No vehicles waiting"}</div>}
		</div>
	);
}

// Shows one configured Gate or Bay using the live-overview projection.
function CheckpointCard({ onAssignVisit, item, timezone, slaMinutes, onViewDetails, canReleaseBay, onReleaseBay, canTerminateTrip, onTerminateTrip, heldForQueued, detail = false, selected = false, onClose, onManageTrip }) {
	const { checkpoint, liveState, latestEvent, operational, cameras = [] } = item;
	const camera = cameras[0];
	const state = item.status || (String(checkpoint.checkpointType).toLowerCase().includes("gate") ? "GATE" : "UNKNOWN");
	const tripId = liveState?.assignmentStatus === "UNASSIGNED" || liveState?.assignmentStatus === "ASSIGNING" ? null : liveState?.tripId || latestEvent?.tripId;
	const operation = String(operational?.operation || "").toUpperCase();
	const isUnload = operation === "UNLOADING";

	// Occupied dwell timer. reservedAt covers RESERVED so the countdown still ticks pre-arrival.
	const sinceIso = state === "OCCUPIED" ? liveState?.occupiedAt : state === "RESERVED" ? liveState?.reservedAt : null;
	const elapsedMs = useElapsedSince(sinceIso);
	const elapsedMinutes = elapsedMs != null ? elapsedMs / 60000 : null;
	const overSla = state === "OCCUPIED" && elapsedMinutes != null && elapsedMinutes > slaMinutes;
	const isBayCheckpoint = String(checkpoint.checkpointType).toLowerCase() === "bay";
	// Release recovers an occupied Bay after a missed departure without ending the trip.
	const showReleaseAction = canReleaseBay && isBayCheckpoint && state === "OCCUPIED";
	const showTerminateAction = canTerminateTrip && state === "OCCUPIED";
	const showActionRow = showReleaseAction || showTerminateAction;
	return (
		// A <div role="button"> rather than a native <button> — the Release Bay action below is itself
		// a real <button>, and nesting interactive controls inside a <button> is invalid HTML.
		<div
			role={detail ? undefined : "button"}
			tabIndex={detail ? undefined : 0}
			aria-haspopup={detail ? undefined : "dialog"}
			className={`dock-checkpoint-card${detail ? " dock-bay-detail" : " dock-checkpoint-card-clickable"}${selected ? " dock-checkpoint-card-selected" : ""}`}
			onClick={detail ? undefined : () => onViewDetails(checkpoint._id)}
			onKeyDown={detail ? undefined : (event) => {
				if (event.target !== event.currentTarget) return;
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					onViewDetails(checkpoint._id);
				}
			}}
		>
			<div className="dock-checkpoint-card-header">
				<span className="dock-checkpoint-id" id={detail ? "dock-bay-detail-title" : undefined}>{checkpoint.displayName}</span>
				{detail && <div className="dock-bay-header-actions">
					{onManageTrip && <button type="button" className="dock-drawer-link dock-bay-manage-trip" onClick={onManageTrip}><OpenInNewIcon fontSize="small" /> Manage Trip</button>}
					<IconButton className="dock-bay-close" size="small" onClick={onClose} aria-label="Close bay details"><CloseIcon fontSize="small" /></IconButton>
				</div>}
				<span className={`dock-status-indicator dock-status-indicator-${state.toLowerCase()}`}><i />{state}</span>
				{detail && <div className="dock-bay-header-meta">
					<div className="dock-bay-update"><span>Last camera update</span><strong>{formatDockTime(camera?.snapshotAt, timezone)}</strong></div>
					<div className="dock-bay-update"><span>Last event</span><strong>{latestEvent?.hideFromUi ? "Gate In not detected" : formatDockTime(latestEvent?.eventAt || liveState?.lastEventAt, timezone)}</strong></div>
				</div>}
			</div>
			<>
				<CameraSnapshot camera={camera} checkpointName={checkpoint.displayName} />
				<div className="dock-camera-status"><span className={camera?.online ? "online" : "offline"} />{camera?.displayName || camera?.cameraId || "No camera configured"}</div>
			</>
			<div className="dock-card-body">
				{!detail && state === "VACANT" && <div className="dock-card-note dock-card-note-vacant">Vacant — ready to assign</div>}
				{state === "RESERVED" && (
					<>
						<div className="dock-card-note dock-card-note-reserved">Reserved{liveState?.vehicleNumber ? ` — ${liveState.vehicleNumber}` : ""}</div>
						{elapsedMs != null && <div className="dock-detail-row"><span>Reserved for</span><strong className="dock-timer">{formatElapsed(elapsedMs, true)}</strong></div>}
						<div className="dock-detail-row"><span>Holding for</span><strong>{heldForQueued ? "Queued vehicle" : "Awaiting arrival"}</strong></div>
					</>
				)}
				{state !== "VACANT" && state !== "RESERVED" && (
					<>
						<div className="dock-detail-row dock-card-vehicle-row">
							<span className="dock-card-vehicle">{liveState?.vehicleNumber || "No vehicle"}</span>
							{["LOADING", "UNLOADING"].includes(operation) && <span className={`dock-optag ${isUnload ? "dock-optag-unload" : "dock-optag-load"}`}>{isUnload ? "Unloading" : "Loading"}</span>}
							{["UNASSIGNED", "ASSIGNING"].includes(liveState?.assignmentStatus) && <small className="dock-card-identification">Identification required <button type="button" className="dock-card-action-btn dock-card-action-assign" aria-label="Assign vehicle" onClick={event => { event.stopPropagation(); onAssignVisit?.(liveState.bayVisitId); }}>Assign </button></small>}
						</div>
						{state === "OCCUPIED" && elapsedMs != null && (
							<div className="dock-detail-row"><span>Cycle</span><strong className="dock-timer">{formatElapsed(elapsedMs, true)}</strong></div>
						)}
						<div className="dock-detail-row"><span>Door</span><strong className={operational?.doorState === "OPEN" ? "dock-doorflag-open" : ""}>{operational?.doorState || "—"}</strong></div>
						<div className="dock-detail-row"><span>Event Type</span><strong>{latestEvent?.hideFromUi ? "Gate In not detected" : String(latestEvent?.eventType || "—").replaceAll("_", " ")}</strong></div>
					</>
				)}
				{!detail && <>
					<div className="dock-detail-row"><span>Last camera update</span><strong>{formatDockTime(camera?.snapshotAt, timezone)}</strong></div>
					<div className="dock-detail-row"><span>Last event</span><strong>{latestEvent?.hideFromUi ? "Gate In not detected" : formatDockTime(latestEvent?.eventAt || liveState?.lastEventAt, timezone)}</strong></div>
				</>}
			</div>
			{showActionRow && (
				<div className="dock-card-actions">
					{showTerminateAction && (
						<button
							type="button"
							className="dock-card-action-btn dock-card-action-terminate"
							onClick={(event) => {
								event.stopPropagation();
								onTerminateTrip(tripId, checkpoint.displayName, liveState?.vehicleNumber);
							}}
						>
							Terminate Trip
						</button>
					)}
					{showReleaseAction && (
						<button
							type="button"
							className="dock-card-action-btn dock-card-action-release"
							onClick={(event) => {
								event.stopPropagation();
								onReleaseBay(checkpoint._id, checkpoint.displayName);
							}}
						>
							Release Bay
						</button>
					)}
				</div>
			)}
		</div>
	);
}

// Paper sizing for the trip drawer. Set here rather than in the shared Drawer util (utils/index.jsx),
// which takes no width override today, so this is scoped to this one feature only.
const DRAWER_PAPER_SX = {
	width: { xs: "100%", sm: 420 },
	maxWidth: "100%",
	display: "flex",
	flexDirection: "column",
	overflow: "hidden",
	backgroundColor: "var(--card-bg-color, #fff)",
};

/**
 * Slide-over showing a trip's full checkpoint/milestone timeline without leaving the board.
 * Uses MUI's Drawer directly (rendered through a portal to document.body) instead of a hand-rolled
 * position:fixed element, so it is not subject to any ancestor page layout's overflow/stacking rules.
 */
function TripTimelineDrawer({ tripId, plantId, onClose }) {
	// Client-side SPA transition, same as the board's original "View Trip" button used before this
	// drawer existed. A plain <a href target="_blank"> forces a hard browser navigation instead, which
	// 404s unless the host has an SPA fallback rewriting deep paths to index.html — not verified here.
	const navigate = useNavigate();
	return (
		<MuiDrawer anchor="right" open={Boolean(tripId)} onClose={onClose} PaperProps={{ className: "dock-timeline-drawer", sx: DRAWER_PAPER_SX }}>
			<div className="dock-drawer-header">
				<h3>Trip Timeline</h3>
				<div className="dock-drawer-header-actions">
					{tripId && (
						<button
							type="button"
							className="dock-drawer-link"
							onClick={() => { onClose(); navigate(`/manage-trip?entryId=${tripId}`); }}
							title="Open in Manage Trip for release/terminate actions"
						>
							<OpenInNewIcon fontSize="small" /> Manage Trip
						</button>
					)}
					<IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
				</div>
			</div>
			<div className="dock-drawer-body">
				{tripId && <DockTripOperations tripId={tripId} plantId={plantId} layout="vertical" />}
			</div>
		</MuiDrawer>
	);
}

// Provides the permission-gated live Command Center for configured Dock checkpoints.
export default function DockOperations() {
	const navigate = useNavigate();
	const { plantId, plantName } = usePlantContext();
	const { userPermissions, isFetched } = useUserPermissions();
	const isTabVisible = useTabVisible();
	// Read by the route guard and by every child panel, so it is resolved once here.
	const canViewDock = Boolean(userPermissions?.viewDockOperations?.value);
	const canViewAnalytics = Boolean(userPermissions?.viewDockAnalytics?.value);
	const visibleTabs = DOCK_TABS.filter(tab => tab.key !== "analytics" || canViewAnalytics);
	const [searchParams, setSearchParams] = useSearchParams();
	// An unknown ?tab= falls back to the live board rather than rendering nothing.
	const requestedTab = searchParams.get("tab");
	const activeTab = visibleTabs.some((item) => item.key === requestedTab) ? requestedTab : DOCK_TABS[0].key;
	const selectTab = (key) => {
		const next = new URLSearchParams(searchParams);
		// The live board is the default, so it stays on a clean URL.
		if (key === DOCK_TABS[0].key) next.delete("tab"); else next.set("tab", key);
		setSearchParams(next, { replace: true });
	};
	const [statusFilter, setStatusFilter] = useState("all");
	const [assignVisitId, setAssignVisitId] = useState(null);
	const [drawerTripId, setDrawerTripId] = useState(null);
	const [selectedCheckpointId, setSelectedCheckpointId] = useState(null);
	const [releaseTarget, setReleaseTarget] = useState(null); // { checkpointId, checkpointName }
	const [releaseReason, setReleaseReason] = useState("");
	const [releaseDockBay, releaseDockBayResult] = useReleaseDockBayMutation();
	const [terminateTarget, setTerminateTarget] = useState(null); // { tripId, checkpointName, vehicleNumber }
	const [terminateDockTrip, terminateDockTripResult] = useTerminateDockTripMutation();
	useRoomForDevice(); // Joins plant_<plantId>, which is the room VMS emits dock changes to.
	const query = useGetDockOperationsOverviewQuery({ plantId }, {
		skip: !plantId || !canViewDock,
		pollingInterval: isTabVisible ? DOCK_FALLBACK_POLL_MS : 0,
	});
	useDockChangeRefetch(plantId, query.refetch);
	const response = query.data;
	const checkpoints = response?.data || [];
	const selectedCheckpoint = checkpoints.find((item) => item.checkpoint._id === selectedCheckpointId);
	const selectedTripId = selectedCheckpoint?.status === "VACANT" || ["UNASSIGNED", "ASSIGNING"].includes(selectedCheckpoint?.liveState?.assignmentStatus) ? null : selectedCheckpoint?.liveState?.tripId || selectedCheckpoint?.latestEvent?.tripId;
	const queueRows = response?.queue?.data || [];
	// Waiting, provisionally closed and finalization-pending trips. They own no Bay card and no queue
	// row, so the board would otherwise lose sight of them entirely between their two Bay legs.
	const provisionalRows = response?.provisional?.data || [];
	const provisionalClosedCount = provisionalRows.filter((row) => row.dockLifecycle?.status !== "WAITING_SECOND_BAY").length;
	// Bays reserved for a vehicle still in the queue read differently from one already admitted and driving over.
	const queuedTripIds = new Set(queueRows.map((row) => String(row.tripId)));
	// Effective priority order means the first row is not necessarily the longest wait, so take the oldest arrival.
	const oldestQueueInTime = queueRows.reduce((oldest, row) => (row.inTime && (!oldest || new Date(row.inTime) < new Date(oldest)) ? row.inTime : oldest), null);
	const longestQueueWaitMs = oldestQueueInTime ? Date.now() - new Date(oldestQueueInTime).getTime() : null;
	// Gates report status: null (no live state); only exclusive Bays participate in occupancy/utilisation.
	const bays = checkpoints.filter((item) => item.status !== null);
	const occupied = bays.filter((item) => item.status === "OCCUPIED").length;
	const reserved = bays.filter((item) => item.status === "RESERVED").length;
	const vacant = bays.filter((item) => item.status === "VACANT").length;
	const doorsOpen = checkpoints.filter((item) => item.operational?.doorState === "OPEN").length;
	const utilisationPercent = bays.length ? Math.round((occupied / bays.length) * 100) : 0;
	// Snapshot at render time, not a live tick like each card's own dwell timer — good enough for a strip total.
	// plantConfig.dockSlaMinutes, with the constant only as a fallback, so every breach test on this
	// page — the cards, this tile, and the operations table — uses one number.
	const slaMinutes = Number(response?.thresholds?.slaMinutes) || DOCK_SLA_BREACH_MINUTES;
	const slaBreaches = bays.filter((item) => item.status === "OCCUPIED" && item.liveState?.occupiedAt
		&& (Date.now() - new Date(item.liveState.occupiedAt).getTime()) / 60000 > slaMinutes).length;
	const visibleCheckpoints = statusFilter === "all" ? checkpoints : checkpoints.filter((item) => item.status === statusFilter);
	// Same permission Manage Trip gates its "Force End Trip" button on — releasing a Bay or force-ending
	// a trip from the dock board is the same class of override action.
	const canReleaseBay = Boolean(userPermissions?.forceEndTrip?.value);
	const canTerminateTrip = Boolean(userPermissions?.forceEndTrip?.value);

	const closeReleaseDialog = () => {
		setReleaseTarget(null);
		setReleaseReason("");
	};

	const handleConfirmRelease = () => {
		if (!releaseReason.trim()) return toastMessage(false, "Reason is required.");
		releaseDockBay({ plantId, checkpointId: releaseTarget.checkpointId, reason: releaseReason.trim() })
			.unwrap()
			.then((res) => {
				toastMessage(!res?.error, res?.message || "Bay released successfully.");
				if (!res?.error) {
					closeReleaseDialog();
					query.refetch();
				}
			})
			.catch((err) => toastMessage(false, err?.data?.message || err?.message || "Some error occurred. Please try again later."));
	};

	const closeTerminateDialog = () => setTerminateTarget(null);

	const handleConfirmTerminate = () => {
		terminateDockTrip({ vehicleEntryId: terminateTarget.tripId, forceEndTrip: true })
			.unwrap()
			.then((res) => {
				const result = res?.data?.[0];
				const success = result ? result.success : !res?.error;
				toastMessage(success, result?.message || res?.message || "Trip terminated successfully.");
				if (success) {
					closeTerminateDialog();
					query.refetch();
				}
			})
			.catch((err) => toastMessage(false, err?.data?.message || err?.message || "Some error occurred. Please try again later."));
	};

	if (!isFetched) return <Loader size="2rem" />;
	if (!canViewDock) return <Navigate to="/not-authorized" replace />;
	if (query.isLoading) return <Loader size="2rem" />;

	return (
		<div className="dock-operations-page">
			<div className="dock-page-heading checkpoint_panel">
				<div>
					<h1 className="checkpoint_heading">Dock Operations</h1>
					<span>{plantName || "Selected warehouse"}</span>
				</div>
				<div className="dock-last-updated"><span>Last updated</span><strong>{formatDockTime(response?.generatedAt, response?.timezone)}</strong></div>
			</div>

			<div className="dock-tabs" role="tablist" aria-label="Dock Operations views">
				{visibleTabs.map((item) => (
					<button
						key={item.key}
						type="button"
						role="tab"
						className="dock-tab"
						aria-selected={activeTab === item.key}
						onClick={() => selectTab(item.key)}
					>
						{item.label}
					</button>
				))}
			</div>

			{(query.error || response?.error) && <div className="dock-message error">{response?.message || "Live Dock Operations could not be loaded."}</div>}

			{/*
			 * One tab's content at a time, rendered rather than hidden with CSS: unmounting Peak
			 * Activity and Recent Operations while the analytics tab is open stops their polling,
			 * which is a real saving on a screen left open all shift. The drawers below stay mounted
			 * at page level, so a deep link into a trip timeline still opens on either tab.
			 */}
			{/*
			 * Two independent panels, stacked rather than wrapped in an OverviewAnalytics component:
			 * a wrapper that owns no shared state would be a file that renders two children. It earns
			 * its place when the range or the Bay selection drives both, and not before.
			 */}
			{activeTab === "analytics" && <>
				<DockUtilisation plantId={plantId} canView={canViewAnalytics} />
				<DwellBreakdown plantId={plantId} canView={canViewAnalytics} />
			</>}

			{activeTab === "live" && <>
			<section className="dock-kpi-row" aria-label="Dock KPIs">
				<div className="dock-kpi dock-kpi-blue">
					<span className="dock-kpi-label">Utilisation</span>
					<strong>{utilisationPercent}<small>%</small></strong>
					{/* "now" is load-bearing: the analytics tab reports utilisation as busy hours over
					    the day, and these are two different measurements under one word. */}
					<span className="dock-kpi-sub">{occupied} of {bays.length} bays occupied now</span>
				</div>
				<div className="dock-kpi dock-kpi-blue">
					<span className="dock-kpi-label">Occupied</span>
					<strong>{occupied}<small>/{bays.length}</small></strong>
					<span className="dock-kpi-sub">{reserved} reserved · {vacant} vacant</span>
				</div>
				<div className="dock-kpi dock-kpi-green">
					<span className="dock-kpi-label">Vacant</span>
					<strong>{vacant}</strong>
					<span className="dock-kpi-sub">Ready to assign</span>
				</div>
				<div className="dock-kpi dock-kpi-purple">
					<span className="dock-kpi-label">Reserved</span>
					<strong>{reserved}</strong>
					<span className="dock-kpi-sub">Bays held</span>
				</div>
				<div className="dock-kpi dock-kpi-orange">
					<span className="dock-kpi-label">Doors Open</span>
					<strong>{doorsOpen}</strong>
					<span className="dock-kpi-sub">of {checkpoints.length} Bays</span>
				</div>
				{/* <div className={`dock-kpi dock-kpi-red${slaBreaches ? " dock-kpi-alert" : ""}`}>
					<span className="dock-kpi-label">SLA Breaches</span>
					<strong>{slaBreaches}</strong>
					<span className="dock-kpi-sub">Cycle &gt; {slaMinutes} min</span>
				</div> */}
				<div className="dock-kpi dock-kpi-purple">
					<span className="dock-kpi-label">Queue</span>
					<strong>{queueRows.length}</strong>
					<span className="dock-kpi-sub">{longestQueueWaitMs != null ? `Longest ${formatElapsed(longestQueueWaitMs, true)}` : "No vehicles waiting"}</span>
				</div>
				{/*
				 * Counted separately, never folded into occupancy or completed totals: these vehicles
				 * have physically left their Bay, so nothing above them is holding a dock, and their
				 * trips are still open, so nothing has completed either.
				 */}
				{Boolean(provisionalRows.length) && <div className="dock-kpi dock-kpi-orange">
					<span className="dock-kpi-label">Awaiting second bay</span>
					<strong>{response?.provisional?.count ?? provisionalRows.length}</strong>
					<span className="dock-kpi-sub">{provisionalClosedCount} provisionally closed</span>
				</div>}
			</section>

			<div className="dock-filter-chips" role="group" aria-label="Filter by status">
				{STATUS_FILTERS.map((filter) => (
					<button
						key={filter.key}
						type="button"
						className="dock-chip"
						aria-pressed={statusFilter === filter.key}
						onClick={() => setStatusFilter(filter.key)}
					>
						{filter.label}
					</button>
				))}
			</div>

			<div className="dock-board-split">
				<div>
					{visibleCheckpoints.length ? (
						<section className="dock-checkpoint-grid">
							{visibleCheckpoints.map((item) => (
								<CheckpointCard onAssignVisit={setAssignVisitId}
									key={item.checkpoint._id}
									item={item}
									timezone={response.timezone}
									slaMinutes={slaMinutes}
									onViewDetails={setSelectedCheckpointId}
									selected={selectedCheckpointId === item.checkpoint._id}
									canReleaseBay={canReleaseBay}
									onReleaseBay={(checkpointId, checkpointName) => setReleaseTarget({ checkpointId, checkpointName })}
									canTerminateTrip={canTerminateTrip}
									onTerminateTrip={(tripId, checkpointName, vehicleNumber) => setTerminateTarget({ tripId, checkpointName, vehicleNumber })}
									heldForQueued={queuedTripIds.has(String(item.liveState?.tripId || ""))}
								/>
							))}
						</section>
					) : <div className="dock-empty">{checkpoints.length ? "No checkpoints match this filter." : "No checkpoints are configured for the Command Center."}</div>}
				</div>
				<QueuePanel key={plantId} rows={queueRows} checkpoints={checkpoints} onViewTimeline={setDrawerTripId} />
			</div>

			<PeakActivity plantId={plantId} canView={canViewDock} />

			<RecentOperations
				plantId={plantId}
				canView={canViewDock}
				onViewTimeline={setDrawerTripId}
			/>
			</>}

			<MuiDrawer anchor="right" open={Boolean(selectedCheckpoint)} onClose={() => setSelectedCheckpointId(null)}
				PaperProps={{ className: "dock-bay-drawer", role: "dialog", "aria-modal": true, "aria-labelledby": "dock-bay-detail-title", sx: { width: { xs: "100%", sm: 420 }, maxWidth: "100%" } }}>
				{selectedCheckpoint && <>
					<CheckpointCard onAssignVisit={setAssignVisitId} item={selectedCheckpoint} timezone={response.timezone} detail onClose={() => setSelectedCheckpointId(null)}
						onManageTrip={selectedTripId ? () => navigate(`/manage-trip?entryId=${selectedTripId}`) : undefined}
						canReleaseBay={canReleaseBay} onReleaseBay={(checkpointId, checkpointName) => setReleaseTarget({ checkpointId, checkpointName })}
						canTerminateTrip={canTerminateTrip} onTerminateTrip={(tripId, checkpointName, vehicleNumber) => setTerminateTarget({ tripId, checkpointName, vehicleNumber })}
						heldForQueued={queuedTripIds.has(String(selectedCheckpoint.liveState?.tripId || ""))} />
					<div className="dock-bay-history">
						{selectedTripId && <DockTripOperations key={selectedTripId} tripId={selectedTripId} plantId={plantId} layout="vertical" />}
					</div>
				</>}
			</MuiDrawer>
			{
				userPermissions.manageUnassignedVisits?.value ? 
			<UnassignedVisits checkpoints={checkpoints} canDelete={canReleaseBay} selectedVisitId={assignVisitId} clearSelectedVisit={() => setAssignVisitId(null)} plantId={plantId} enabled={canViewDock} onAssigned={() => query.refetch()} />
			: null}
			{activeTab === "live" && <ProvisionalTrips plantId={plantId} enabled={canViewDock} onViewTimeline={setDrawerTripId} />}
			<TripTimelineDrawer tripId={drawerTripId} plantId={plantId} onClose={() => setDrawerTripId(null)} />

			{releaseTarget && (
				<Dialog
					size="sm"
					open={true}
					title={`Release Bay — ${releaseTarget.checkpointName}`}
					handleClose={closeReleaseDialog}
					handleCancel={closeReleaseDialog}
					handleSubmit={handleConfirmRelease}
					SubmitText="Release"
					isSubmitDisabled={releaseDockBayResult.isLoading}
				>
					<Input
						id="dock-release-reason"
						name="releaseReason"
						label="Reason"
						placeholder="Why is this Bay being released?"
						value={releaseReason}
						onChange={(event) => setReleaseReason(event.target.value)}
						required
					/>
				</Dialog>
			)}

			{terminateTarget && (
				<Dialog
					size="sm"
					open={true}
					title={`Terminate Trip — ${terminateTarget.checkpointName}`}
					handleClose={closeTerminateDialog}
					handleCancel={closeTerminateDialog}
					handleSubmit={handleConfirmTerminate}
					SubmitText="Terminate"
					isSubmitDisabled={terminateDockTripResult.isLoading}
				>
					<p>
						This ends the trip{terminateTarget.vehicleNumber ? ` for ${terminateTarget.vehicleNumber}` : ""} and frees any Bay
						it currently holds. This cannot be undone.
					</p>
				</Dialog>
			)}
		</div>
	);
}
